import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import i18n from '../i18n';
import { Candidate } from '../models/job';
import {
  analyzeCandidateSocialResource,
  SocialResourcePlatform,
  SocialResourceScoringResult,
} from '../services/job.service';
import { getEnhancedDataValue, parseEnhancedData } from './useEnhancedData';

export type SocialPlatform = SocialResourcePlatform;

const PLATFORMS: readonly SocialPlatform[] = [
  'leetcode',
  'github',
  'google_scholar',
  'blog',
] as const;

const STORED_KEY: Record<SocialPlatform, string> = {
  leetcode: 'leetcode_analysis',
  github: 'github_analysis',
  google_scholar: 'google_scholar_analysis',
  blog: 'blog_analysis',
};

const HUMAN_LABEL: Record<SocialPlatform, string> = {
  leetcode: 'LeetCode',
  github: 'GitHub',
  google_scholar: 'Google Scholar',
  blog: 'blog',
};

type AnalysisMap = Record<SocialPlatform, SocialResourceScoringResult | null>;
type FlagMap = Record<SocialPlatform, boolean>;

interface SseNotificationDetail {
  event: string;
  message?: string;
  details?: Record<string, unknown>;
}

const emptyAnalyses = (): AnalysisMap => ({
  leetcode: null,
  github: null,
  google_scholar: null,
  blog: null,
});

const STUCK_ANALYZING_TIMEOUT_MS = 90_000;

const emptyFlags = (): FlagMap => ({
  leetcode: false,
  github: false,
  google_scholar: false,
  blog: false,
});

export interface UseCandidateSocialScoringResult {
  analyses: AnalysisMap;
  isAnalyzing: FlagMap;
  analyze: (platform: SocialPlatform) => Promise<void>;
}

/**
 * Owns the per-candidate social-resource scoring state:
 *   - reads stored analyses from enhancedData
 *   - listens for `invirtus:sse-notification` (re-broadcast in App.tsx) and
 *     applies started/completed/failed updates per platform
 *   - exposes an `analyze(platform)` action that triggers the async pipeline
 */
export const useCandidateSocialScoring = (
  candidate: Candidate
): UseCandidateSocialScoringResult => {
  const { getAccessTokenSilently } = useAuth0();

  const storedAnalyses = useMemo<AnalysisMap>(() => {
    const parsed = parseEnhancedData(candidate?.enhancedData);
    const next = emptyAnalyses();
    for (const platform of PLATFORMS) {
      next[platform] =
        getEnhancedDataValue<SocialResourceScoringResult>(parsed, STORED_KEY[platform]) ?? null;
    }
    return next;
  }, [candidate?.enhancedData]);

  const [analyses, setAnalyses] = useState<AnalysisMap>(storedAnalyses);
  const [isAnalyzing, setIsAnalyzing] = useState<FlagMap>(emptyFlags);

  // SSE delivery for the completed/failed event isn't guaranteed (e.g. a
  // reconnect gap), so back every "analyzing" flag with a timer that force-
  // clears it rather than leaving the spinner stuck forever.
  const analyzingTimersRef = useRef<Record<SocialPlatform, ReturnType<typeof setTimeout> | null>>({
    leetcode: null,
    github: null,
    google_scholar: null,
    blog: null,
  });

  const setAnalyzing = useCallback((platform: SocialPlatform, value: boolean) => {
    const existingTimer = analyzingTimersRef.current[platform];
    if (existingTimer) {
      clearTimeout(existingTimer);
      analyzingTimersRef.current[platform] = null;
    }
    setIsAnalyzing((prev) => ({ ...prev, [platform]: value }));
    if (value) {
      analyzingTimersRef.current[platform] = setTimeout(() => {
        analyzingTimersRef.current[platform] = null;
        setIsAnalyzing((prev) => ({ ...prev, [platform]: false }));
      }, STUCK_ANALYZING_TIMEOUT_MS);
    }
  }, []);

  useEffect(() => {
    const timers = analyzingTimersRef.current;
    return () => {
      for (const platform of PLATFORMS) {
        const timer = timers[platform];
        if (timer) clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    setAnalyses(storedAnalyses);
  }, [candidate.id, storedAnalyses]);

  useEffect(() => {
    const handleSseNotification = (rawEvent: Event) => {
      const customEvent = rawEvent as CustomEvent<SseNotificationDetail>;
      const notification = customEvent.detail;
      if (!notification?.event || !notification.details) return;

      const eventCandidateId = String(notification.details.candidate_id ?? '');
      if (eventCandidateId !== candidate.id) return;

      const platform = String(notification.details.platform ?? '').toLowerCase() as SocialPlatform;
      if (!PLATFORMS.includes(platform)) return;

      if (notification.event === 'social_profile_scoring_started') {
        setAnalyzing(platform, true);
        return;
      }

      if (notification.event === 'social_profile_scoring_completed') {
        const analysis = notification.details.analysis as SocialResourceScoringResult | undefined;
        if (analysis) {
          setAnalyses((prev) => ({ ...prev, [platform]: analysis }));
        }
        setAnalyzing(platform, false);
        toast.success(
          i18n.t('candidates:notifications.social_scored', { platform: HUMAN_LABEL[platform] }),
        );
        return;
      }

      if (notification.event === 'social_profile_scoring_failed') {
        setAnalyzing(platform, false);
        const label = HUMAN_LABEL[platform];
        const fallback = i18n.t('candidates:notifications.social_score_failed', { platform: label });
        const raw = String(notification.details.error ?? notification.message ?? '');
        toast.error(raw.trim() ? raw : fallback);
      }
    };

    window.addEventListener('invirtus:sse-notification', handleSseNotification);
    return () => {
      window.removeEventListener('invirtus:sse-notification', handleSseNotification);
    };
  }, [candidate.id, setAnalyzing]);

  const analyze = useCallback(
    async (platform: SocialPlatform): Promise<void> => {
      const label = HUMAN_LABEL[platform];
      try {
        setAnalyzing(platform, true);
        const accessToken = await getAccessTokenSilently();
        const response = await analyzeCandidateSocialResource(accessToken, candidate.id, platform);
        if (response.error || !response.data) {
          toast.error(
            response.error ?? i18n.t('candidates:notifications.social_score_failed', { platform: label }),
          );
          setAnalyzing(platform, false);
          return;
        }
        toast.info(i18n.t('candidates:notifications.social_scoring_started', { platform: label }));
      } catch (error) {
        console.error(`Error scoring ${label} profile:`, error);
        toast.error(i18n.t('candidates:notifications.social_score_failed', { platform: label }));
        setAnalyzing(platform, false);
      }
    },
    [candidate.id, getAccessTokenSilently, setAnalyzing]
  );

  return { analyses, isAnalyzing, analyze };
};
